import { createApiClient } from '../../core/api-client.js';
import { handleCliError } from '../../core/api-error.js';
import { requireProjectId } from '../../core/auth.js';
import { writeResult } from '../../core/output.js';
import {
  getDefaultProject,
  clearDefaultProject,
} from '../../lib/config-file.js';
import type { GlobalOptions } from '../../types/global.js';

// === Programmatic API ===

export interface ListProjectsOptions {
  cursor?: string;
  limit?: number;
}

export async function listProjects(options: ListProjectsOptions = {}) {
  const client = createApiClient();
  const { data, error } = await client.GET('/api/projects', {
    params: { query: { cursor: options.cursor, limit: options.limit } },
  });
  if (error) throw new Error(error.error?.message || 'Failed to list projects');
  return data;
}

export async function getProject(options: { projectId?: string } = {}) {
  const id = options.projectId ?? requireProjectId();
  const client = createApiClient();
  const { data, error } = await client.GET('/api/projects/{projectId}', {
    params: { path: { projectId: id } },
  });
  if (error) throw new Error(error.error?.message || 'Failed to get project');
  return data;
}

export async function createProject(options: { name: string }) {
  const client = createApiClient();
  const { data, error } = await client.POST('/api/projects', {
    body: { name: options.name },
  });
  if (error)
    throw new Error(error.error?.message || 'Failed to create project');
  return data;
}

export async function updateProject(options: {
  projectId?: string;
  name: string;
}) {
  const id = options.projectId ?? requireProjectId();
  const client = createApiClient();
  const { data, error } = await client.PATCH('/api/projects/{projectId}', {
    params: { path: { projectId: id } },
    body: { name: options.name },
  });
  if (error)
    throw new Error(error.error?.message || 'Failed to update project');
  return data;
}

export async function deleteProject(options: { projectId?: string } = {}) {
  const id = options.projectId ?? requireProjectId();
  const client = createApiClient();
  const { data, error } = await client.DELETE('/api/projects/{projectId}', {
    params: { path: { projectId: id } },
  });
  if (error)
    throw new Error(error.error?.message || 'Failed to delete project');
  // Drop a now-stale default. Compare against the resolved `id`, not
  // `options.projectId`, so deleting the default with no explicit id (resolved
  // via requireProjectId()) still clears it.
  if (getDefaultProject() === id) clearDefaultProject();
  return data ?? { success: true };
}

// === CLI Command Handlers ===

interface ProjectsCommandOptions extends GlobalOptions {
  json?: boolean;
  output?: string;
  project?: string;
  name?: string;
  cursor?: string;
  limit?: number;
}

async function handleResult(
  fn: () => Promise<unknown>,
  options: ProjectsCommandOptions,
): Promise<void> {
  try {
    const result = await fn();
    await writeResult(JSON.stringify(result, null, 2), options);
  } catch (error) {
    handleCliError(error);
  }
}

export async function listProjectsCommand(
  options: ProjectsCommandOptions,
): Promise<void> {
  await handleResult(
    () => listProjects({ cursor: options.cursor, limit: options.limit }),
    options,
  );
}

export async function getProjectCommand(
  projectId: string | undefined,
  options: ProjectsCommandOptions,
): Promise<void> {
  await handleResult(
    () => getProject({ projectId: projectId ?? options.project }),
    options,
  );
}

export async function createProjectCommand(
  name: string,
  options: ProjectsCommandOptions,
): Promise<void> {
  await handleResult(() => createProject({ name }), options);
}

export async function updateProjectCommand(
  projectId: string | undefined,
  options: ProjectsCommandOptions,
): Promise<void> {
  const name = options.name;
  if (!name) {
    throw new Error('Missing required option: --name <name>');
  }
  await handleResult(
    () =>
      updateProject({
        projectId: projectId ?? options.project,
        name,
      }),
    options,
  );
}

export async function deleteProjectCommand(
  projectId: string | undefined,
  options: ProjectsCommandOptions,
): Promise<void> {
  await handleResult(
    () => deleteProject({ projectId: projectId ?? options.project }),
    options,
  );
}
